<script lang="ts">
    import { createConcurrencyLimiter } from '@/js/utils/concurrencyLimiter';

    // Module scope: one limiter shared by every graph widget, so a dashboard with many of them
    // does not fetch all their data (and trigger all the backend KPI resolution) at once.
    const widgetDataLimiter = createConcurrencyLimiter(4);
</script>

<script setup lang="ts">
    import { IWidget } from '../../services/api/widget/types';
    import DonutChart from './chartTypes/DonutChart.vue';
    import ColumnChart from './chartTypes/ColumnChart.vue';
    import LineChart from './chartTypes/LineChart.vue';
    import PieChart from './chartTypes/PieChart.vue';
    import PolarChart from './chartTypes/PolarChart.vue';
    import GraphWidgetErrorState from './GraphWidgetErrorState.vue';
    import InvalidFilterEmptyState from './InvalidFilterEmptyState.vue';
    import {
        isDonutChart,
        isLineChart,
        isColumnChart,
        isPieChart,
        isPolarChart,
        isGraphWidgetDataLoading,
    } from '@/js/utils/widgetUtils';
    import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
    import axios from 'axios';
    import LoadingDots from '../LoadingDots.vue';
    import { getDashboardWidget } from '@/js/services/api/dashboard/dashboardWidgetApi';

    const props = defineProps<{
        widget: IWidget<'Graph'>;
        dashboardId?: string;
        canEdit?: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'removeInvalidFilter'): void;
    }>();

    // The slim dashboard fetch returns graph widgets without data; each widget loads its own.
    const widget = ref<IWidget<'Graph'>>(props.widget);
    const isLoading = ref(false);
    const loadFailed = ref(false);

    const hasError = computed(
        () => loadFailed.value || Boolean(widget.value.graphWidget?.hasError)
    );

    let abortController: AbortController | null = null;

    onMounted(async () => {
        if (!props.dashboardId || !isGraphWidgetDataLoading(props.widget)) {
            return;
        }
        isLoading.value = true;
        abortController = new AbortController();
        try {
            const full = await widgetDataLimiter(() =>
                getDashboardWidget(
                    { dashboardId: props.dashboardId!, widgetId: props.widget.id },
                    abortController!.signal
                )
            );
            widget.value = { ...props.widget, ...full } as IWidget<'Graph'>;
        } catch (error) {
            if (!axios.isCancel(error)) {
                loadFailed.value = true;
            }
        } finally {
            isLoading.value = false;
        }
    });

    onBeforeUnmount(() => abortController?.abort());
</script>

<template>
    <LoadingDots v-if="isLoading" class="h-full" />
    <GraphWidgetErrorState v-else-if="hasError" />
    <div
        v-else-if="widget.graphWidget?.filterSelection?.levelFilterMatchesNothing"
        class="h-full px-4 pb-4"
    >
        <InvalidFilterEmptyState
            class="rounded-lg bg-gray-lightest/60"
            :removable="props.canEdit"
            @remove="emit('removeInvalidFilter')"
        />
    </div>
    <DonutChart
        v-else-if="isDonutChart(widget) && widget.graphWidget?.graphData.donutChart"
        :data="widget.graphWidget!.graphData.donutChart"
    />
    <ColumnChart
        v-else-if="isColumnChart(widget) && widget.graphWidget?.graphData.columnChart"
        :data="widget.graphWidget!.graphData.columnChart"
    />
    <LineChart
        v-else-if="isLineChart(widget) && widget.graphWidget?.graphData.lineChart"
        :data="widget.graphWidget!.graphData.lineChart"
    />
    <PieChart
        v-else-if="isPieChart(widget) && widget.graphWidget?.graphData.pieChart"
        :data="widget.graphWidget!.graphData.pieChart"
    />
    <PolarChart
        v-else-if="isPolarChart(widget) && widget.graphWidget?.graphData.polarChart"
        :data="widget.graphWidget!.graphData.polarChart"
    />
</template>
